import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

/**
* Hook that will create a new pagination
*/
function usePagination (filters, pagination, pageableObject, columns, primaryKeys, sortColumnFieldPageable, sortOptions, filterText, columnFilters, queueOrder, columnFilterOperators) {
  const [currentPagination, setCurrentPagination] = useState({})

  useEffect(() => {
    let newPagination
    let paramFilters = []
    let parameterValues = []
    let parameterNames = []

    if (filters !== undefined) {
      filters.forEach(filter => {
        parameterValues = [...parameterValues, filter.key]
        paramFilters = [...paramFilters, filter.field]
        parameterNames = [...parameterNames, filter.valueName]
      })
    }
    const result = new Map()
    if (pagination !== undefined) {
      newPagination = { ...pagination }
      newPagination.page = pageableObject.number
      newPagination.size = pageableObject.size
    } else {
      columns?.forEach(column => {
        if (!result.has(column.field)) {
          result.set(column.field, [])
        }
        if (column.includeColumnFilters) {
          result.get(column.field).push(...column.includeColumnFilters)
        }
      })
      newPagination = {
        parameterFilters: paramFilters,
        parameterFiltersValues: parameterValues,
        parameterFiltersNames: parameterNames,
        page: pageableObject.number,
        size: pageableObject.size,
        columns: columns.map((column) => column.field),
        mixedColumnFilters: JSON.stringify(Object.fromEntries(result)),
        genericSearch: '',
        genericSearchExcludedColumns: columns
          .filter(col => col.disableGenericSearch)
          .map(col => col.field),
        excludedColumns: columns
          .filter(col => col.disableFilter)
          .map(col => col.field),
        rows: [],
        allSelected: false,
        primaryKeys,
        sortKey: [],
        sortMode: []
      }
    }
    /* Sorting */
    const filterColumn = columns.find(col => col.field === sortColumnFieldPageable.field)

    if (filterColumn !== undefined || (sortColumnFieldPageable && sortColumnFieldPageable.field === queueOrder.field)) {
      newPagination.sortKey = [sortColumnFieldPageable.field]
      newPagination.sortMode = [sortColumnFieldPageable.direction]
    }

    if (sortOptions !== undefined && Array.isArray(sortOptions)) {
      sortOptions.forEach(sortOption => {
        newPagination.sortKey = [...newPagination.sortKey, sortOption.field]
        newPagination.sortMode = [...newPagination.sortMode, sortOption.direction]
      })
    }

    /* Generic filter */
    if (filterText !== undefined && filterText !== '') {
      newPagination.genericSearch = filterText
    }

    /* Column filters */
    if (columnFilters !== undefined && Object.keys(columnFilters).length !== 0) {
      // Map every parallel array by the same field keys/order so they never drift apart.
      // The operator defaults to '' (contains/LIKE) for text columns, keeping the request
      // backward compatible with backends unaware of the operator dimension.
      const fields = Object.keys(columnFilters)
      newPagination.columnFilters = fields
      newPagination.columnFilterValues = fields.map((field) => columnFilters[field])
      newPagination.columnFilterOperators = fields.map((field) => (columnFilterOperators && columnFilterOperators[field]) || '')
    }

    setCurrentPagination(newPagination)
  }, [filters, pagination, pageableObject.size, pageableObject.number, columns, primaryKeys, sortColumnFieldPageable, sortOptions, filterText, columnFilters, queueOrder?.field, columnFilterOperators])

  return currentPagination
}

export default usePagination

usePagination.propTypes = {
  /**
   * Request pagination object.Can be generated by usePagination hook. Is requiered
   */
  requestPagination: PropTypes.any.isRequired,
  /**
   * Multiple selection value object. Is requiered
   */
  multipleSelectionValue: PropTypes.any.isRequired,
  /**
   * Extra filter parameter object. Is requiered
   */
  extraFilterParameter: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.bool.isRequired
  })
}
